import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LibraryGate } from "../components/LibraryGate";
import api from "../lib/api";
import { usd } from "../lib/money";

const mean = (items, key) => items.reduce((sum, item) => sum + item[key], 0) / (items.length || 1);
const dollars = (value) => usd(value, 4);

const TrialTable = ({ report }) => {
  const baseline = report.renders.filter((r) => r.k === 3);
  return <table data-testid="reference-trial-table" className="w-full text-xs sm:text-sm table-fixed">
    <caption className="text-left text-muted-foreground py-3">Mean of 3 photos · render only · estimated USD</caption>
    <thead><tr className="text-left border-b border-border"><th className="py-3">Refs</th><th>Cost</th><th>Extra cost</th><th>Time</th><th>Extra time</th></tr></thead>
    <tbody>{[3, 6, 9].map((k) => {
      const rows = report.renders.filter((r) => r.k === k);
      return <tr key={k} data-testid={`reference-trial-row-${k}`} className="border-b border-border">
        <th className="text-left py-4">{k}</th><td>{dollars(mean(rows, "usd"))}</td><td>{dollars(mean(rows, "usd") - mean(baseline, "usd"))}</td>
        <td>{mean(rows, "seconds").toFixed(1)}s</td><td>+{(mean(rows, "seconds") - mean(baseline, "seconds")).toFixed(1)}s</td>
      </tr>;
    })}</tbody>
  </table>;
};

export default function ReferenceTrialPage() {
  const [report, setReport] = useState(null);
  const [image, setImage] = useState(null);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/library/reference-trial");
      const sheet = await api.get("/library/reference-trial/comparison.jpg", { responseType: "blob" });
      setReport(data); setImage(URL.createObjectURL(sheet.data)); setLocked(false);
    } catch (e) {
      if (e?.response?.status === 401) setLocked(true);
      else setError("This preview comparison is not available in this environment.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (image) URL.revokeObjectURL(image); }, [image]);
  return <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-6" data-testid="reference-trial-page">
    <Link to="/library" className="text-sm text-accent hover:underline" data-testid="trial-back-link">Back to library</Link>
    <h1 className="font-heading font-extrabold text-4xl sm:text-5xl">More refs. Better ducks?</h1>
    <p className="text-sm text-muted-foreground">Private preview trial · same three photos · 3, 6 and 9 style refs · production defaults unchanged</p>
    {locked ? <LibraryGate onGranted={load} /> : error ? <p role="alert" data-testid="trial-error">{error}</p> : !report ? <p data-testid="trial-loading">Loading comparison…</p> : <>
      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-5" data-testid="trial-verdict">
        <h2 className="font-semibold text-base">Recommendation: keep 3 in production</h2>
        <p className="text-sm mt-2">6 refs changed the olive jacket to bright green; 9 lost that subject’s beard. Hair colour also drifted on the tiara subject. Larger sets have not passed the likeness gate.</p>
      </div>
      <img data-testid="reference-trial-comparison" src={image} alt="Three source photos, each beside its Light 3, Standard 6 and Rich 9 duck renders" className="w-full rounded-2xl border border-border" />
      <a data-testid="trial-download" href={image} download="Duckify3D-reference-comparison.jpg" className="inline-block text-accent font-semibold hover:underline">Download full-size comparison</a>
      <TrialTable report={report} />
      <p data-testid="trial-cost-notes" className="text-sm text-muted-foreground">Nine renders plus shared matching: {dollars(report.total_estimated_usd || 0)} estimated. All used Flash, no fallback. Matching adds {mean(report.subjects, "pick_seconds").toFixed(1)}s and {dollars(mean(report.subjects, "pick_cost_usd"))} per photo. Each photo’s ranking was picked once, then nested 3/6/9 sets reused its classic anchor and the same four printed refs. This isolates count, not picker variance.</p>
      <p className="text-xs text-muted-foreground" data-testid="trial-method">Measured provider call times; public token-rate cost estimates, not Universal Key invoices. Three photos and one render per setting are directional evidence, not a statistical guarantee. No sculpting was performed.</p>
    </>}
  </main>;
}
